<template>
  <EditableComponentContent
    v-bind="componentProps"
    :expired-content="expiredContent"
    :restricted-content="restrictedContent"
    @add="emit('add')"
    @asset-change="emit('asset-change', $event)"
    @content-change="emit('content-change', $event)"
    @discard="emit('discard')"
    @drop="emit('drop', $event)"
    @duplicate="emit('duplicate')"
    @edit="emit('edit')"
    @remove="emit('remove')"
    @resize-end="emit('resize-end')"
    @resize-start="emit('resize-start')"
    @select="emit('select')"
    @settings="emit('settings')"
    @style="emit('style', $event)"
    @variant-change="emit('variant-change', $event)"
  >
    <template #toolbar-slot>
      <slot name="toolbar-slot" />
    </template>
    <slot />
  </EditableComponentContent>
</template>
<script setup lang="ts">
import { type DropdownOption } from 'naive-ui'
import { computed, inject, type CSSProperties, type ComputedRef } from 'vue'

import EditableComponentContent from './EditableComponentContent.vue'
import { type ThemeComponentOptionEnum } from '@canvas-builder/composables/useCanvasTheme'
import type { ComponentNode, ComponentTypes } from '@canvas-builder/types/canvas'
import type { BlockContext } from '@canvas-builder/components/ui/Block/Block.types'
import type { ResizeHandlePosition } from './ResizeHandles.vue'

const props = withDefaults(
  defineProps<{
    id: ComponentNode['id']
    discard?: boolean // allow discard mode
    duplicate?: boolean // allow duplicate mode
    edit?: boolean // allow edit mode
    remove?: boolean // allow remove mode
    settings?: boolean // allow settings mode
    stylable?: boolean // allow style mode
    collapsible?: boolean // allow collapsible mode
    showOnlyTopToolbar?: boolean // force top toolbar
    showOnlyBottomToolbar?: boolean // force bottom toolbar
    immovable?: boolean // allow collapsible mode
    excludeStylables?: ThemeComponentOptionEnum[] // a list of theming options that should not be visible
    add?: boolean // allow add mode
    active?: boolean // set as active editing mode
    selected?: boolean // set as selected component
    inlineEdit?: boolean // mark as inline editing mode e.g. text -- for now only styling changes
    hideOutline?: boolean // hide outline
    addIcon?: string // add icon
    editIcon?: string // edit icon
    discardIcon?: string // discard icon
    duplicateIcon?: string // duplicate icon
    parentType?: ComponentTypes // parent type
    settingsIcon?: string // settings icon
    removeIcon?: string // remove icon
    resizable?: boolean // allow resizable mode
    resizeHandles?: ResizeHandlePosition[] // list of handlers to show
    resizeMaintainAspectRatio?: boolean
    expiredContent?: boolean
    restrictedContent?: boolean
    variants?: {
      title: string
      options: DropdownOption[]
      selected?: DropdownOption['key']
    } // variants for the dropdown
  }>(),
  {
    collapsible: true,
  },
)
const emit = defineEmits<{
  'asset-change': [{ assetId: string }]
  'content-change': [{ fileId: string; pageIndex?: number; pageId?: string; thumbnailUrl?: string }]
  discard: []
  duplicate: []
  edit: []
  remove: []
  add: []
  select: []
  settings: []
  style: [v: CSSProperties]
  drop: [{ draggedId: string; position: 'top' | 'bottom' | 'left' | 'right' }]
  'variant-change': [variant: DropdownOption['key']]
  'resize-start': []
  'resize-end': []
}>()

const blockCtx = inject<BlockContext>(
  'usedInBlockCtx',
  computed(() => ({})),
)

const attachTo = inject<ComputedRef<HTMLElement | string | undefined>>(
  'attachTo',
  computed(() => undefined),
)

const componentProps = computed(() => {
  if (blockCtx.value.usedInBlockId) {
    return {
      ...props,
      to: attachTo.value,
      duplicate: false,
      remove: false,
      edit: false,
      resizable: false,
      settings: false,
      stylable: false,
      collapsible: false,
      immovable: false,
      showOnlyTopToolbar: false,
      showOnlyBottomToolbar: false,
      active: false,
      add: false,
      discard: false,
      variants: undefined,
    }
  }

  return { ...props, to: attachTo.value }
})
</script>
